import { useMemo } from 'react';
import useSearchParamState, {
    useArraySearchParamState,
    useBooleanSearchParamState,
} from '../../../core/utils/useSearchParamState.tsx';

// sentinel value for filtering by "no agent connected", distinct from any real agent name
export const NO_AGENT_VALUE = '__no_agent__';

interface FilterableMailboxRow {
    mailInbox: string;
    type: 'shared' | 'personal';
    enabled: boolean;
    agentNames: string[];
}

// Search, sort, and Type/Status/Agent filtering for the mailboxes overview table. State is
// synced to URL search params so the current view is shareable/bookmarkable.
export const useMailboxFilters = <T extends FilterableMailboxRow>(rows: T[]) => {
    const [search, setSearch] = useSearchParamState('search', '');
    const [sortBy, setSortBy] = useSearchParamState<'mailbox' | 'type'>('sortBy', 'mailbox');
    const [sortDirection, setSortDirection] = useSearchParamState<'asc' | 'desc'>('sort', 'asc');
    const [showFilters, setShowFilters] = useBooleanSearchParamState('filtersOpen', false);
    const [typeFilter, setTypeFilter] = useArraySearchParamState('type');
    const [statusFilter, setStatusFilter] = useArraySearchParamState('status');
    const [agentFilter, setAgentFilter] = useArraySearchParamState('agent');
    const hasActiveFilters = typeFilter.length > 0 || statusFilter.length > 0 || agentFilter.length > 0;

    const visibleRows = useMemo(() => {
        const filtered = rows.filter((row) => {
            if (search && !row.mailInbox.toLowerCase().includes(search.toLowerCase())) return false;
            if (typeFilter.length && !typeFilter.includes(row.type)) return false;
            if (statusFilter.length && !statusFilter.includes(row.enabled ? 'active' : 'paused')) return false;
            if (agentFilter.length) {
                const matchesAgent = row.agentNames.some((name) => agentFilter.includes(name));
                const matchesNoAgent = row.agentNames.length === 0 && agentFilter.includes(NO_AGENT_VALUE);
                if (!matchesAgent && !matchesNoAgent) return false;
            }
            return true;
        });

        const sign = sortDirection === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const primary = sortBy === 'type' ? a.type.localeCompare(b.type) : a.mailInbox.localeCompare(b.mailInbox);
            if (primary !== 0) return primary * sign;
            return sortBy === 'type' ? a.mailInbox.localeCompare(b.mailInbox) * sign : 0;
        });
    }, [rows, search, sortBy, sortDirection, typeFilter, statusFilter, agentFilter]);

    return {
        search,
        setSearch,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        showFilters,
        setShowFilters,
        typeFilter,
        setTypeFilter,
        statusFilter,
        setStatusFilter,
        agentFilter,
        setAgentFilter,
        hasActiveFilters,
        visibleRows,
    };
};

export default useMailboxFilters;
